/**
 * Onglet Dignités — Domicile, exaltation, exil, chute
 * Reproduit fidèlement le tableau du site PHP :
 * - 3 cartes résumé (forces / neutres / difficultés)
 * - Tableau : forces d'abord, puis difficultés, puis neutres
 * - Synthèse en bas
 */

import { useState } from 'react';
import { colors } from '../../styles/tokens.js';
import { PLANET_SYMBOLS } from './planetColors.js';
import type { ChartAnalysis, DigniteResult } from '../../types/clients.js';

type DignitesData = NonNullable<ChartAnalysis['dignites']>;

const DIGNITE_STYLES: Record<string, { color: string; bg: string; rowBg: string; icon: string }> = {
  'Domicile':   { color: '#166534', bg: '#dcfce7', rowBg: '#f0fdf4', icon: '🏠' },
  'Exaltation':  { color: '#1e40af', bg: '#dbeafe', rowBg: '#f0fdf4', icon: '⬆️' },
  'Exil':        { color: '#991b1b', bg: '#fee2e2', rowBg: '#fef2f2', icon: '🚪' },
  'Chute':       { color: '#92400e', bg: '#fef3c7', rowBg: '#fef2f2', icon: '⬇️' },
  'Pérégrine':   { color: '#6b7280', bg: '#f3f4f6', rowBg: 'transparent', icon: '⚖️' },
};

/** Signification par dignité — aligné avec le PHP */
const SIGNIFICATIONS: Record<string, string> = {
  'Domicile': "Expression naturelle et puissante. La planète est « chez elle ».",
  'Exaltation': "La planète est amplifiée, en position de force maximale.",
  'Exil': "Expression moins naturelle. Travail nécessaire pour intégrer l'énergie.",
  'Chute': "La planète peine à s'exprimer. Défi à transformer en force.",
  'Pérégrine': "Ni renforcée ni affaiblie. Expression dépendante des aspects.",
};

interface Props {
  dignites: DignitesData;
}

export function DignitesTab({ dignites }: Props) {
  const [showHelp, setShowHelp] = useState(false);

  return (
    <div>
      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: '1.25rem', paddingBottom: '0.75rem',
        borderBottom: `2px solid ${colors.border}`,
      }}>
        <h3 style={{ margin: 0, fontSize: '1.1rem', color: colors.text, fontWeight: 700 }}>
          <i className="bi bi-award-fill" style={{ color: colors.accent, marginRight: 8 }} />
          Carte des Dignités
        </h3>
        <button
          onClick={() => setShowHelp(!showHelp)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '5px 12px', fontSize: '0.8rem', fontWeight: 500,
            background: showHelp ? colors.accent : '#f3f4f6',
            color: showHelp ? '#fff' : colors.textMid,
            border: 'none', borderRadius: 6, cursor: 'pointer',
            transition: 'all 0.2s', fontFamily: 'inherit',
          }}
        >
          <i className="bi bi-question-circle" />
          Aide
        </button>
      </div>

      {/* Aide */}
      {showHelp && (
        <div style={{
          background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 10,
          padding: '1.25rem', marginBottom: '1.25rem', fontSize: '0.85rem',
          lineHeight: 1.7, color: '#1e3a5f',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#0c4a6e' }}>
              Comprendre les Dignites
            </h4>
            <button
              onClick={() => setShowHelp(false)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: '1.2rem', color: '#64748b', lineHeight: 1,
              }}
            >
              &times;
            </button>
          </div>

          <p style={{ margin: '0 0 12px' }}>Les dignites decrivent la <strong>qualite d'expression</strong> d'une planete selon le signe qu'elle occupe. Une planete bien placee s'exprime plus facilement ; une planete mal placee demande un travail d'integration.</p>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <p style={{ fontWeight: 700, marginBottom: 4 }}>
                {'\uD83C\uDFE0'} Domicile
              </p>
              <p style={{ margin: '0 0 4px 0' }}>La planete est dans le signe qu'elle gouverne. Elle est « chez elle » et s'exprime avec aisance et naturel.</p>
              <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748b' }}>Exemples : Soleil en Lion, Lune en Cancer, Mars en Belier, Venus en Taureau ou Balance...</p>
            </div>

            <div>
              <p style={{ fontWeight: 700, marginBottom: 4 }}>
                {'\u2B06\uFE0F'} Exaltation
              </p>
              <p style={{ margin: '0 0 4px 0' }}>La planete est dans un signe ou ses qualites sont amplifiees. Position de force maximale, parfois excessive.</p>
              <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748b' }}>Exemples : Soleil en Belier, Lune en Taureau, Mars en Capricorne, Venus en Poissons...</p>
            </div>

            <div>
              <p style={{ fontWeight: 700, marginBottom: 4 }}>
                {'\uD83D\uDEAA'} Exil (Detriment)
              </p>
              <p style={{ margin: '0 0 4px 0' }}>La planete est dans le signe oppose a son domicile. Son expression naturelle est contrariee, demandant un effort d'adaptation.</p>
              <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748b' }}>Exemples : Soleil en Verseau, Lune en Capricorne, Mars en Balance, Venus en Scorpion...</p>
            </div>

            <div>
              <p style={{ fontWeight: 700, marginBottom: 4 }}>
                {'\u2B07\uFE0F'} Chute
              </p>
              <p style={{ margin: '0 0 4px 0' }}>La planete est dans le signe oppose a son exaltation. Position de faiblesse qui devient un defi a transformer en force.</p>
              <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748b' }}>Exemples : Soleil en Balance, Lune en Scorpion, Mars en Cancer, Venus en Vierge...</p>
            </div>

            <div>
              <p style={{ fontWeight: 700, marginBottom: 4 }}>
                {'\u2696\uFE0F'} Peregrine
              </p>
              <p style={{ margin: 0 }}>La planete n'est ni en dignite ni en debilite. Son expression depend des aspects qu'elle recoit.</p>
            </div>

            <div>
              <p style={{ fontWeight: 700, marginBottom: 4 }}>
                {'\uD83D\uDCA1'} Point important
              </p>
              <p style={{ margin: 0 }}>Une planete en « difficulte » n'est pas une malediction ! Elle indique simplement un domaine ou un travail conscient est necessaire. Souvent, ce sont ces planetes qui, une fois integrees, deviennent les plus grandes forces du theme.</p>
            </div>
          </div>
        </div>
      )}

      {/* Résumé visuel — identique au PHP */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
        <div style={{
          padding: '1rem', borderRadius: 8, textAlign: 'center',
          background: 'linear-gradient(135deg, #dcfce7, #bbf7d0)',
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.25rem' }}>💪</div>
          <div style={{ fontWeight: 700, fontSize: '1.5rem', color: '#166534' }}>{dignites.forces.length}</div>
          <div style={{ fontSize: '0.85rem', color: '#15803d' }}>Planètes en force</div>
        </div>
        <div style={{
          padding: '1rem', borderRadius: 8, textAlign: 'center',
          background: '#f3f4f6',
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.25rem' }}>⚖️</div>
          <div style={{ fontWeight: 700, fontSize: '1.5rem', color: '#374151' }}>{dignites.neutres.length}</div>
          <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>Planètes neutres</div>
        </div>
        <div style={{
          padding: '1rem', borderRadius: 8, textAlign: 'center',
          background: 'linear-gradient(135deg, #fee2e2, #fecaca)',
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.25rem' }}>🔧</div>
          <div style={{ fontWeight: 700, fontSize: '1.5rem', color: '#991b1b' }}>{dignites.difficultes.length}</div>
          <div style={{ fontSize: '0.85rem', color: '#b91c1c' }}>Planètes en difficulté</div>
        </div>
      </div>

      {/* Tableau complet — ordre PHP : forces, difficultés, neutres */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: `2px solid ${colors.border}` }}>
              <th style={thStyle}>Planète</th>
              <th style={thStyle}>Position</th>
              <th style={thStyle}>Dignité</th>
              <th style={thStyle}>Signification</th>
            </tr>
          </thead>
          <tbody>
            {dignites.forces.map((d) => (
              <DigniteRow key={d.planete} d={d} />
            ))}
            {dignites.difficultes.map((d) => (
              <DigniteRow key={d.planete} d={d} />
            ))}
            {dignites.neutres.map((d) => (
              <DigniteRow key={d.planete} d={d} />
            ))}
          </tbody>
        </table>
      </div>

      {/* Synthèse — identique au PHP */}
      {(() => {
        const nbForces = dignites.forces.length;
        const nbDiff = dignites.difficultes.length;
        let texte: string;
        let couleur: string;
        let bgColor: string;

        if (nbForces > nbDiff + 1) {
          texte = 'Le thème dispose de nombreuses ressources planétaires bien placées.';
          couleur = '#166534';
          bgColor = '#dcfce7';
        } else if (nbDiff > nbForces + 1) {
          texte = "Plusieurs planètes demandent un travail d'intégration conscient.";
          couleur = '#991b1b';
          bgColor = '#fee2e2';
        } else {
          texte = 'Équilibre entre planètes en force et planètes en défi.';
          couleur = '#6b7280';
          bgColor = '#f3f4f6';
        }

        return (
          <div style={{ marginTop: '1.5rem', padding: '1rem', background: bgColor, borderRadius: 8 }}>
            <div style={{ fontWeight: 700, color: couleur, marginBottom: '0.25rem' }}>
              <i className="bi bi-lightbulb" style={{ marginRight: 6 }} />
              Synthèse des dignités
            </div>
            <p style={{ margin: 0, color: couleur, fontSize: '0.9rem' }}>{texte}</p>
          </div>
        );
      })()}
    </div>
  );
}

function DigniteRow({ d }: { d: DigniteResult }) {
  const pSymbol = PLANET_SYMBOLS[d.planete] ?? '•';
  const dStyle = DIGNITE_STYLES[d.label] ?? DIGNITE_STYLES['Pérégrine']!;
  const signification = SIGNIFICATIONS[d.label] ?? 'Expression dépendante des aspects et du contexte.';

  return (
    <tr style={{ background: dStyle.rowBg }}>
      <td style={cellStyle}>
        <span style={{ fontFamily: "'Noto Sans Symbols 2', sans-serif", fontSize: '1.2rem', marginRight: 8 }}>{pSymbol}</span>
        <strong>{d.planete}</strong>
        {d.retrograde && (
          <span style={{ color: '#9333ea', fontSize: '0.8rem', marginLeft: 4 }}>℞</span>
        )}
      </td>
      <td style={cellStyle}>
        <span style={{ fontWeight: 500 }}>{d.signe}</span>
        <span style={{ color: colors.textMuted, fontSize: '0.85rem' }}> M{d.maison}</span>
      </td>
      <td style={cellStyle}>
        <span style={{
          display: 'inline-block', padding: '4px 10px', borderRadius: 12,
          background: dStyle.bg, color: dStyle.color,
          fontSize: '0.8rem', fontWeight: 600,
        }}>
          {dStyle.icon} {d.label}
        </span>
      </td>
      <td style={{ ...cellStyle, fontSize: '0.85rem', color: dStyle.color }}>
        {signification}
      </td>
    </tr>
  );
}

/** Styles du tableau — identiques au PHP */
const thStyle: React.CSSProperties = {
  padding: '12px 8px', textAlign: 'left',
  background: colors.bgPage, borderBottom: `2px solid ${colors.border}`,
  fontWeight: 600, fontSize: '0.85rem',
};

const cellStyle: React.CSSProperties = {
  padding: '12px 8px', borderBottom: `1px solid ${colors.border}`,
};
